import type { ViewerShell } from '../../../shell';
import type { AssistantApi } from '../api';
import type { MessageHandlers } from '../messages';
import type { AssistantPanel } from '../panel';
import type { ToolsApi } from './api';

/** What every Asistente tool gets from the registration in ../index.ts. */
export interface ToolDeps {
  shell: ViewerShell;
  panel: AssistantPanel;
  api: ToolsApi;
  assistantApi: AssistantApi;
  /** The conversation's own handlers: a finding a tool shows is reviewed exactly like one in an answer. */
  handlers: MessageHandlers;
}

/** Text only: nothing a tool shows is ever parsed as HTML. */
export function el<K extends keyof HTMLElementTagNameMap>(tag: K, className: string, text?: string): HTMLElementTagNameMap[K] {
  const node = document.createElement(tag);
  node.className = className;
  if (text !== undefined) node.textContent = text;
  return node;
}

export function button(label: string, className: string, onClick: () => void, title?: string): HTMLButtonElement {
  const b = el('button', `assistant-btn ${className}`, label);
  b.type = 'button';
  if (title) b.title = title;
  b.addEventListener('click', onClick);
  return b;
}

/** A result card for the conversation log: a title and a body the tool fills. */
export function resultCard(kind: string, title: string): { card: HTMLElement; body: HTMLElement } {
  const card = el('div', `assistant-msg assistant-msg-assistant assistant-tool-result tool-${kind}`);
  card.dataset.tool = kind;
  card.appendChild(el('div', 'assistant-tool-title', title));
  const body = el('div', 'assistant-tool-body');
  card.appendChild(body);
  return { card, body };
}

const MM_DECIMALS = 10;

/** "12.3 mm" (one decimal), or "—" when unknown. */
export function mm(value: number | null): string {
  return value === null ? '—' : `${(Math.round(value * MM_DECIMALS) / MM_DECIMALS).toFixed(1)} mm`;
}

/** "12.3 × 8.1 mm" from a long and a short axis. */
export function axes(long: number | null, short: number | null): string {
  if (long === null) return '—';
  return short === null ? mm(long) : `${mm(long).replace(' mm', '')} × ${mm(short)}`;
}
